import React, { useEffect, useRef } from "react";
import { FiX } from "react-icons/fi";
import { Check, Crown, Hourglass, Sparkles } from "lucide-react";

const ICONS = { crown: Crown, hourglass: Hourglass, sparkles: Sparkles };

/**
 * Centred upgrade dialog shared by the trial welcome, trial ended and custom
 * prompts nudges. Escape, the close button and a backdrop click call onClose,
 * which defaults to the secondary choice.
 */
const UpgradeDialog = ({
  open,
  tone = "info", // "info" | "warn"
  icon = "crown",
  title,
  body,
  bullets,
  example,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  onClose,
}) => {
  const primaryRef = useRef(null);
  const dismiss = onClose || onSecondary;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss?.();
    };
    document.addEventListener("keydown", onKey);
    primaryRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  const Icon = ICONS[icon] || Crown;
  const warn = tone === "warn";

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-[#0000007a] backdrop-blur-[2px] px-4 font-inter"
      onClick={dismiss}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-dialog-title"
        className="relative w-full max-w-[440px] rounded-2xl bg-white px-6 pt-8 pb-5 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.35)] motion-safe:animate-[upgradeDialogIn_220ms_ease-out_both]"
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes upgradeDialogIn {
            from { opacity: 0; transform: translateY(12px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3.5 right-3.5 inline-flex items-center justify-center w-8 h-8 rounded-full text-[#64748B] hover:bg-[#F1F5F9] transition-colors"
        >
          <FiX size={18} />
        </button>

        <span
          className={`mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-full ${
            warn ? "bg-[#FFF4E0] text-[#E08A00]" : "bg-[#E6F5FE] text-[#2F9BF0]"
          }`}
          aria-hidden="true"
        >
          <Icon size={28} strokeWidth={2} />
        </span>

        <h2
          id="upgrade-dialog-title"
          className="text-center text-xl font-bold text-[#0F172A] leading-snug tracking-[-0.01em]"
        >
          {title}
        </h2>

        {body && (
          <p className="mt-2 text-center text-[15px] leading-[1.55] text-[#475569]">{body}</p>
        )}

        {example && (
          <div className="mt-4 rounded-xl bg-[#F1FAFF] border border-[#D6ECFB] px-4 py-3 text-[14px] leading-[1.6] text-[#475569]">
            {example}
          </div>
        )}

        {bullets?.length > 0 && (
          <ul className="mt-5 grid gap-2.5 px-1">
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-2.5 text-[14.5px] text-[#0F172A]">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#DCFCE7] text-[#16A34A] shrink-0">
                  <Check size={13} strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>
        )}

        <button
          ref={primaryRef}
          type="button"
          onClick={onPrimary}
          className="mt-6 w-full rounded-xl bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] py-3 text-[15.5px] font-semibold text-white transition-colors"
        >
          {primaryLabel}
        </button>
        {secondaryLabel && (
          <button
            type="button"
            onClick={onSecondary}
            className="mt-2 w-full rounded-xl py-2.5 text-[15px] font-semibold text-[#2F9BF0] hover:bg-[#F1FAFF] transition-colors"
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default UpgradeDialog;
